import Grid from '@mui/material/Grid'

import { MatchResultsMachine } from './match-results-machine'
import { RcButton } from '@/components/molecules/interactive/RcButton'
import { ModalConfiguration } from '@/components/organisms/modal/ModalConfiguration'
import { RcTrans } from '@/components/atoms/RcTrans'
import { ConfirmingButton } from '@/components/molecules/interactive/ConfirmingButton'

type ActionType = {
  guard: () => boolean
  action: () => void
  text: () => React.ReactElement
  disabled: () => boolean
  isError?: boolean
  isOutlined?: boolean
}

export const MatchResultFlowActions = ({
  machine,
  title,
  subtitle,
  topContent,
  screenshots = [],
  onAnalyzeAllScreenshots
}: {
  machine: MatchResultsMachine
  title: React.ReactNode
  subtitle?: React.ReactNode
  topContent?: React.ReactNode
  screenshots?: any[]
  onAnalyzeAllScreenshots?: () => void
}) => {
  const [state, send] = machine

  // Built-in screenshot actions for uploadScreenshots state
  const isUploadScreenshots = state.matches('uploadScreenshots')
  const canSkip = state.can('SKIP_SCREENSHOTS')
  const canAnalyze =
    screenshots.length > 0 &&
    screenshots.some(
      (s: any) => !s.analysisResult && !s.error && !s.isAnalyzing
    )
  const isAnalyzing = screenshots.some((s: any) => s.isAnalyzing)
  const allAnalyzedOrErrored =
    screenshots.length > 0 &&
    screenshots.every((s: any) => s.analysisResult || s.error)

  // collection of all possible button actions for state machine and when/how to render them
  const actions: (ActionType | null)[] = [
    // Screenshot step actions (skip left, analyze right)
    ...(isUploadScreenshots
      ? ([
          canSkip
            ? {
                guard: () => true,
                action: () => send('SKIP_SCREENSHOTS'),
                text: () => (
                  <RcTrans i18nKey='competition:skip-screenshot-step' />
                ),
                isOutlined: true,
                disabled: () => false
              }
            : null,
          canAnalyze && onAnalyzeAllScreenshots
            ? {
                guard: () => true,
                action: onAnalyzeAllScreenshots,
                text: () => (
                  <RcTrans i18nKey='competition:analyze-all-screenshots' />
                ),
                disabled: () =>
                  screenshots.length === 0 ||
                  isAnalyzing ||
                  allAnalyzedOrErrored
              }
            : null
        ].filter(Boolean) as ActionType[])
      : [
          // Default state machine actions (not in uploadScreenshots)
          {
            guard: () => state.can('CONFLICT'),
            action: () => send('CONFLICT'),
            text: () => (
              <RcTrans i18nKey='competition:match.results-start-conflict' />
            ),
            isError: true,
            isOutlined: false,
            disabled: () => false
          },
          {
            guard: () => state.can('DECLINE'),
            action: () => send('DECLINE'),
            text: () => (
              <RcTrans i18nKey='competition:match.results-request-admin' />
            ),
            isError: true,
            isOutlined: false,
            disabled: () => false
          },
          {
            guard: () => state.can('ACCEPT'),
            action: () => send('ACCEPT'),
            text: () => <RcTrans i18nKey='competition:match.results-accept' />,
            isOutlined: false,
            disabled: () => false
          },
          {
            guard: () => state.matches('sourcedGames'),
            action: () => send('SKIP_SOURCED_GAMES'),
            text: () => (
              <RcTrans i18nKey='competition:match.results-manually-submit' />
            ),
            isOutlined: false,
            disabled: () => false
          },
          {
            guard: () => state.can('BACK_TO_SOURCED_GAMES'),
            action: () => send('BACK_TO_SOURCED_GAMES'),
            text: () => (
              <RcTrans i18nKey='competition:match.back-to-sourced-games' />
            ),
            isOutlined: false,
            disabled: () => false
          },
          {
            guard: () =>
              state.can('ADMIN_REPORT_GAMES') && !state.matches('reportScores'),
            action: () => send('ADMIN_REPORT_GAMES'),
            text: () => (
              <RcTrans i18nKey='competition:match.enter-admin-game-results' />
            ),
            isOutlined: false,
            disabled: () => !state.can('ADMIN_REPORT_GAMES')
          },
          {
            guard: () => state.matches('sourcedGames'),
            action: () => send('REPORT_SOURCED_GAMES'),
            text: () => (
              <RcTrans i18nKey='competition:match.results-complete-submit' />
            ),
            isOutlined: false,
            disabled: () => !state.can('REPORT_SOURCED_GAMES')
          }
        ])
  ].flat()
  const filteredActions: ActionType[] = actions.filter(Boolean) as ActionType[]

  const isAdminEntry = false
  return (
    <ModalConfiguration
      title={title}
      subtitle={subtitle}
      topContent={topContent}
    >
      <Grid container direction='row' spacing={3}>
        {filteredActions
          .filter(it => it.guard())
          .map((it, idx) => {
            return (
              <Grid
                item
                xs={filteredActions.filter(a => a.guard()).length > 1 ? 6 : 12}
                key={idx}
              >
                <RcButton
                  onClick={() => {
                    it.action()
                  }}
                  variant={it.isOutlined ? 'outlined' : 'contained'}
                  color={it.isError ? 'error' : 'primary'}
                  fullWidth
                  disabled={it.disabled()}
                >
                  {it.text()}
                </RcButton>
              </Grid>
            )
          })}
      </Grid>
    </ModalConfiguration>
  )
}
